/**
 * useAudio.ts — WEB.4.P2.1 — Convenience hook for components that fire SFX.
 *
 * Returns a stable callback. The hook also keeps Howler's global mute in
 * sync with the store flag every render. Components do NOT need to mount
 * effects for this — they just call `play(id)`.
 *
 * Usage:
 *   const { play, setMuted, muted } = useAudio();
 *   <button onClick={() => { play("dice-roll"); rollDice(); }}>Roll</button>
 */

import { useCallback, useEffect } from "react";
import { useGameStore } from "../store/game-store.js";
import { persistMute, playSfx, setMuted as setHowlerMuted } from "./audio.js";

export interface AudioApi {
  muted: boolean;
  play: (id: string, volume?: number) => void;
  setMuted: (muted: boolean) => void;
}

export function useAudio(): AudioApi {
  const muted = useGameStore((s) => s.audioMuted);
  const setMutedStore = useGameStore((s) => s.setAudioMuted);

  // Keep Howler's global mute in sync with the store flag. Hooked here
  // rather than at the store action so the side-effect lives in the React
  // tree (testable, also no-ops in non-DOM contexts).
  useEffect(() => {
    setHowlerMuted(muted);
  }, [muted]);

  const play = useCallback((id: string, volume?: number) => {
    playSfx(id, volume);
  }, []);

  const setMuted = useCallback(
    (m: boolean) => {
      setMutedStore(m);
      persistMute(m);
    },
    [setMutedStore],
  );

  return { muted, play, setMuted };
}
